/**
 * Selects which interaction row an incoming `bot:ai_responded` answer should
 * fill so the SDR "AI is typing" indicator clears.
 *
 * The dashboard turns the indicator ON for a pending row (visitor `user` set,
 * `ai` empty, no `event`) and clears it once that row's `ai` is filled. The
 * backend reassigns `query` on the email-replay path and emits the raw query
 * while the persisted row is sanitized, so a strict `user === query` match can
 * miss and leave the indicator stuck. We therefore prefer an exact question
 * match but fall back to the OLDEST still-unanswered row (FIFO) — text-agnostic
 * so drift always clears, FIFO so multiple pending turns (e.g. the email-gate
 * replay) pair answers to questions in order instead of mis-pairing. (#2720)
 *
 * Event rows (agent joined/left, human/system messages) carry `ai` set and an
 * empty `user`, so `!ai && user` never selects them.
 *
 * The FIFO fallback additionally skips serialized email-form submissions
 * (`{"email":...}`): such a row is not a question awaiting an answer — it renders
 * as a "Contact details provided" pill that swallows any `ai`. After the
 * email-gate replay the original-question row is already answered, so a
 * text-agnostic fallback would otherwise land the real answer on the email-form
 * row and drop it from the live view. Skipping it pushes the answer as its own
 * row, matching what the persisted thread shows on refetch. The exact match is
 * intentionally NOT filtered, so the email-submit turn's own `ai_responded`
 * (query === the form payload) still fills and clears its row. (#2720, #2727)
 *
 * Returns the index to fill, or -1 when no pending row exists (caller pushes a
 * new interaction).
 */
export interface PendingInteractionLike {
  user?: string;
  ai?: string;
}

// A serialized email-form submission the visitor widget sends, e.g.
// '{"email":"a@b.com","firstName":"A"}'. Mirrors tryParseEmailPayload in
// MessagesContainer (which renders these as a contact-details pill, not a bubble).
function isEmailFormSubmission(user?: string): boolean {
  if (!user || typeof user !== 'string') return false;
  const trimmed = user.trim();
  if (!trimmed.startsWith('{')) return false;
  try {
    const parsed = JSON.parse(trimmed);
    return !!(parsed && typeof parsed === 'object' && (parsed.email || parsed.firstName || parsed.lastName));
  } catch {
    return false;
  }
}

export function findPendingInteractionIndex<T extends PendingInteractionLike>(
  interactions: T[],
  query: string,
): number {
  const exact = interactions.findIndex((i) => !i.ai && i.user === query);
  if (exact !== -1) return exact;
  return interactions.findIndex((i) => !i.ai && i.user && !isEmailFormSubmission(i.user));
}
